import 'reflect-metadata';
import { WeekDays } from '../models/enums/week-days';
import { CachedStoreObject } from '../models/utilities/cached-store-object';
import { AppliedFilterBarOpt, FilterBarInputOpt, IFilterBar } from '../models/utilities/filter-bar';
import { RequestError } from '../models/utilities/requests';
import { SelectListItem } from '../models/utilities/selectlist-item';
import { IPriceSummaryTotal, PriceSummaryTotals } from '../shared/models/price-summary-totals';
import { DeliveryHourView } from '../shared/models/ship-to-address-read';
import { GlobalConstants } from './app-constants';
import { toCondensedDateTime } from './date-utils';

/**
 * A collection of utility methods used throughout the app
 */
export abstract class AppUtils {

    /**
     * Helper for getting/parsing/converter query string string parameters
     */
    public static stringParam = (match: any, key: string) => {
        const qsValue = (match && match.params) ? match.params[key] : undefined;
        return qsValue ? qsValue : '';
    }

    /**
     * Helper for getting/parsing/converter query string numeric parameters
     */
    public static numericParam = (match: any, key: string) => {
        const qsValue = (match && match.params) ? match.params[key] : undefined;
        const qsNumber = parseInt(qsValue, 10);
        return isNaN(qsNumber) ? undefined : qsNumber;
    }

    /**
     * Is the provided object an array?
     * @param object The object to evaluate
     */
    public static isArray(object: any) {

        if (object === undefined || object === null) {
            return false;
        }
        else if (object === Array) {
            return true;
        }
        else if (typeof Array.isArray === "function") {
            return Array.isArray(object);
        }
        else {
            return !!(object instanceof Array);
        }
    }

    /**
     * Is the provided object a JS primitive?
     * @param obj The object to evaluate
     */
    public static isPrimitive(obj: any) {

        switch (typeof obj) {
            case "string":
            case "number":
            case "boolean":
                return true;
        }

        return !!(obj instanceof String || obj === String ||
            obj instanceof Number || obj === Number ||
            obj instanceof Boolean || obj === Boolean);

    }

    // Helper methods for parsing decorated properties
    public static getToClass(target: any, propertyKey: string): any {
        return Reflect.getMetadata("design:type", target, propertyKey)
    }

    /**
     * Converts an enum to a SelectListItem array.
     * It splits the key of the enum on the underscore to provide a 
     * more friendly readable name
     * @param data The enum to convert
     */
    public static enumToSelectList(data: any): Array<SelectListItem<string>> {

        if (AppUtils.IsEmpty(data)) {
            return [];
        }

        const selects: Array<SelectListItem<string>> = [];
        Object.keys(data).forEach(curr => {
            if (typeof curr === "string" && !Number.isNaN(Number(curr))) {
                const enumOpt = data[curr];
                selects.push(new SelectListItem({
                    id: enumOpt.value,
                    name: enumOpt.display
                }));
            }
        });

        return selects;
    }

    /**
     * General check for empty strings or object
     * @param input The input to check
     */
    public static IsEmpty(input: any) {
        return (input === undefined || input == null || input.length === 0) ||
            (Object.keys(input).length === 0 && input.constructor === Object);
    }

    /**
     * Converts the provided string to title casing
     * @param str The string to title case
     */
    public static ToTitleCase(str: string) {
        return str.charAt(0).toUpperCase() + str.substr(1).toLowerCase();
    }

    /**
     * Search one array for instances of another array.
     * Currently used for determining if a user has a role
     * in a given list of roles
     * @param haystack The list to search through
     * @param needles The list of values to look for
     */
    public static DoArraysIntersect(haystack: string[], needles: string[]) {
        let hasMatch = false;
        haystack.forEach((currentRole: string) => {
            needles.forEach((reqRole: string) => {
                if (reqRole === currentRole) {
                    hasMatch = true;
                    return;
                }
            });

            if (hasMatch) {
                return;
            }
        });

        return hasMatch;
    }

    /**
     * Check if the two arrays are equal.
     * @param _arr1 One array
     * @param _arr2 The other array
     */
    public static ArraysEqual(_arr1: any[], _arr2: any[]) {

        if (!Array.isArray(_arr1) || !Array.isArray(_arr2) || _arr1.length !== _arr2.length) {
            return false;
        }

        const arr1 = _arr1.concat().sort();
        const arr2 = _arr2.concat().sort();

        for (let i = 0; i < arr1.length; i++) {
            if (arr1[i] !== arr2[i]) {
                return false;
            }
        }

        return true;
    }

    /**
     * Decycle the provided object 
     * @param obj The plain object to iterate on
     */
    public static Decycle(obj: any) {
        let cache: any[] | null = [];

        const stringified = JSON.stringify(obj, (key, value) => {
            if (typeof value === 'object' && value !== null) {
                if (!cache || cache.indexOf(value) !== -1) {
                    return;
                }

                cache.push(value);
            }

            return value;
        });

        cache = null;

        return stringified;
    }

    /**
     * Return a nicely formatted error message taking into account
     * custom error code and message, falling back to the HTTP
     * code and error, and finally a generic error
     * @param error The RequestError to pull a message from
     */
    public static FormatError(error: any) {
        const err = error as RequestError;
        let errorMsg = '';

        if (err.message) {
            errorMsg = `${err.errorCode} : ${err.message}`;
        }
        else if (err.serverErrors) {
            errorMsg = `${err.statusCode} : ${err.serverErrors[0]}`;
        }
        else {
            errorMsg = 'An unknown error occurred.';
        }

        return errorMsg;
    }

    public static formatPhone(phone: string) {

        const phoneTest = new RegExp(/^((\+1)|1)? ?\(?(\d{3})\)?[ .-]?(\d{3})[ .-]?(\d{4})( ?(ext\.? ?|x)(\d*))?$/);

        if (phone && phone.length > 0) {
            const p = phone.trim();
            const results = phoneTest.exec(p);
            if (results !== null && results.length > 8) {    
                return "(" + results[3] + ") " + results[4] + "-" + results[5] + (typeof results[8] !== "undefined" ? " x" + results[8] : "");        
            }
        }
        else if (phone === null) {
            return '';
        }
        
        return phone;
    }

    /**
     * Check if two strings are anagrams of each other
     * @param str1 The first input string to check
     * @param str2 The second input string to check
     */
    public static IsAnagram(str1: string, str2: string) {

        if (str1.length !== str2.length) {
            return false;
        }

        const sortStr1 = str1.split('').sort().join('');
        const sortStr2 = str2.split('').sort().join('');

        return (sortStr1 === sortStr2);
    }

    public static DeepEquals(a: any, b: any) {
        if (a === b) { return true; }

        if (a && b && typeof a == 'object' && typeof b == 'object') {
            const arrA = Array.isArray(a);
            const arrB = Array.isArray(b);
            let i;
            let length;
            let key;

            if (arrA && arrB) {
                length = a.length;
                if (length != b.length) { return false; }
                for (i = length; i-- !== 0;) {
                    if (!AppUtils.DeepEquals(a[i], b[i])) { return false;
                } }
                return true;
            }

            if (arrA != arrB) { return false; }

            const dateA = a instanceof Date;
            const dateB = b instanceof Date;

            if (dateA != dateB) { return false; }
            if (dateA && dateB) { return a.getTime() == b.getTime(); }

            const regexpA = a instanceof RegExp;
            const regexpB = b instanceof RegExp;

            if (regexpA != regexpB) { return false; }
            if (regexpA && regexpB) { return a.toString() == b.toString(); }

            const keys = Object.keys(a);
            length = keys.length;

            if (length !== Object.keys(b).length) {
                return false;
            }

            for (i = length; i-- !== 0;) {
                if (!Object.prototype.hasOwnProperty.call(b, keys[i])) { return false;
            } }

            for (i = length; i-- !== 0;) {
                key = keys[i];
                if (!AppUtils.DeepEquals(a[key], b[key])) { return false; }
            }

            return true;
        }

        return a !== a && b !== b;
    };

    // Shout out to https://stackoverflow.com/questions/4810841/how-can-i-pretty-print-json-using-javascript
    public static colorizeJson(toColor: object): string {

        let json = JSON.stringify(toColor, null, 2);
        json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

        return json.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, function (match) {
            let cls = 'number';
            if (/^"/.test(match)) {
                if (/:$/.test(match)) {
                    cls = 'key';
                } else {
                    cls = 'string';
                }
            }
            else if (/true|false/.test(match)) {
                cls = 'boolean';
            }
            else if (/null/.test(match)) {
                cls = 'null';
            }

            return '<span class="json-' + cls + '">' + match + '</span>';
        });

    }

    /**
     * Attempt to parse a date from the URL and return a date object and formatted string
     * @returns Returns an object with a Moment date as well as a formatted date str
     */
    public static getDateFromUrl(): Date | undefined {

        const dateRegex = /\d{4}-\d{2}-\d{2}/;
        const path = window.location.pathname;

        const dateQryStr = dateRegex.exec(path);
        if (dateQryStr && dateQryStr.length > 0) {

            const withNoTime = `${dateQryStr[0]}T00:00:00`;

            const fullDate = new Date(withNoTime);
            if(fullDate) {
                return fullDate;
            }
            else {
                return undefined;
            }
        }

        return undefined;
    }

    /**
     * Helper for checking redux cachce validity
     */
    public static isCacheExpired(cacheObj : CachedStoreObject<any>) : boolean {
        if (!cacheObj) {
            return true;
        }

        const currentTime = toCondensedDateTime(new Date());
        return cacheObj.expiresAt < currentTime;
    }

    /**
     * Helper to group the given array by DayOfWeek
     * @param hour The array to group
     */
    public static groupHoursByDayOfWeek(hours : DeliveryHourView[]) {
        
        const grouped : any = {
            1: [],
            2: [],
            4: [],
            8: [],
            16: [],
            32: [],
            64: []
        };

        hours.forEach((itm : DeliveryHourView, idx : number) => {

            const timeStringOpts = {
                hour: '2-digit', 
                minute: '2-digit'
            };

            const startFrm = new Date('1970-01-01T' + itm.startTime)
                                .toLocaleTimeString('en-US',  timeStringOpts);

            const endFrm = new Date('1970-01-01T' + itm.endTime
                                ).toLocaleTimeString('en-US',  timeStringOpts);          

            // tslint:disable-next-line: no-bitwise
            if ((itm.weekDays & WeekDays.Sunday) === WeekDays.Sunday) {
                grouped[WeekDays.Sunday].push({ 
                    start: startFrm,
                    end: endFrm
                });
            }

            // tslint:disable-next-line: no-bitwise
            if ((itm.weekDays & WeekDays.Monday) === WeekDays.Monday) {
                grouped[WeekDays.Monday].push({ 
                    start: startFrm,
                    end: endFrm
                });
            }

            // tslint:disable-next-line: no-bitwise
            if ((itm.weekDays & WeekDays.Tuesday) === WeekDays.Tuesday) {
                grouped[WeekDays.Tuesday].push({ 
                    start: startFrm,
                    end: endFrm
                });
            }

            // tslint:disable-next-line: no-bitwise
            if ((itm.weekDays & WeekDays.Wednesday) === WeekDays.Wednesday) {
                grouped[WeekDays.Wednesday].push({ 
                    start: startFrm,
                    end: endFrm
                });
            }     
            
            // tslint:disable-next-line: no-bitwise
            if ((itm.weekDays & WeekDays.Thursday) === WeekDays.Thursday) {
                grouped[WeekDays.Thursday].push({ 
                    start: startFrm,
                    end: endFrm
                });
            }

            // tslint:disable-next-line: no-bitwise
            if ((itm.weekDays & WeekDays.Friday) === WeekDays.Friday) {
                grouped[WeekDays.Friday].push({ 
                    start: startFrm,
                    end: endFrm
                });
            }

            // tslint:disable-next-line: no-bitwise
            if ((itm.weekDays & WeekDays.Saturday) === WeekDays.Saturday) {
                grouped[WeekDays.Saturday].push({ 
                    start: startFrm,
                    end: endFrm
                });
            }            

        });

        return grouped;
        
    }

    public static groupBy = (array : any[], fn : any) => {

        return array.reduce((result, item) => {

            const key = fn(item);
            if (!result[key]) {
                result[key] = [];
            }
            
            result[key].push(item);
            return result;

      }, {});
    }

    /**
     * Basic greater than/less than compare function
     */
    public static simpleCompare = (a : any, b : any) => {
        if (a > b) {
            return +1;
        }
        
        if (a < b) {
            return -1;
        }

        return 0;
    }

    public static addTimeSpansFromServer = (accumualtorTS: string, serverTS : string) => {
        let timeStr = '';
        if (serverTS.length > 0 && accumualtorTS.length > 0) {
            
            const toAddTo    = accumualtorTS.split(':');
            const toBeAdded  = serverTS.split(':');

            const days      = Number.parseFloat(toAddTo[0]) + Number.parseFloat(toBeAdded[0]);
            const hours     = Number.parseFloat(toAddTo[1]) + Number.parseFloat(toBeAdded[1]);
            const minutes   = Number.parseFloat(toAddTo[2]) + Number.parseFloat(toBeAdded[2]);

            timeStr = `${days}:${hours}:${minutes}`;
        }

        return AppUtils.convertTimeSpanFromServer(timeStr);
    }    

    public static convertTimeSpanFromServer = (serverTS : string) => {
        let timeStr = '';
        if (serverTS.length > 0) {

            let finalHour = 0;
            let finalMin = 0;
            let finalSec = 0;

            // 1.6:77:105

            const allParts  = serverTS.split(':');

            const dayHour   = allParts[0].split('.');
            const minPart = Number.parseInt(allParts[1], 10);            
            const secPart = Number.parseInt(allParts[2], 10);

            // Figure out seconds
            if (secPart >= 60) {
                const addtMinutes = Math.floor(secPart / 60);
                const remainingSecs = secPart % 60;

                finalMin = addtMinutes;
                finalSec = remainingSecs;
            }
            else {
                finalSec = secPart;
            }

            // Figure out minutes
            if (minPart >= 60) {
                const additionalHours = Math.floor(minPart / 60);
                const remainingMinutes = minPart % 60;

                finalHour = additionalHours;
                finalMin += remainingMinutes;
            }
            else {
                finalMin = minPart;
            }

            // Figure out hours
            if (dayHour.length === 2) {
                finalHour += (Number.parseInt(dayHour[0], 10) * 24) + (Number.parseInt(dayHour[1], 10));
            }
            else {
                finalHour += Number.parseInt(dayHour[0], 10);
            }

            timeStr += `${finalHour.toString().padStart(2, '0') }:${finalMin.toString().padStart(2, '0')}:${finalSec.toString().padStart(2, '0')}`;
        }

        return timeStr;
    }

    public static convertTimeSpanToServer = (clientTS? : string) => {
        if (clientTS && clientTS.length > 0) {
            
            const allParts  = clientTS.split(':');
            const part1Num  = Number.parseInt(allParts[0], 10);
            const days      = Math.floor(part1Num / 24);
            const hours     = part1Num % 24;

            const paddedHours = hours.toString().padStart(2, '0');
            const paddedMins = allParts[1].toString().padStart(2, '0');
            const paddedSecs = allParts[2].toString().padStart(2, '0');

            if (days > 0) {
                return `${ days }.${ paddedHours }:${ paddedMins }:${ paddedSecs }`;
            }
            else {
                return `${ paddedHours }:${ paddedMins }:${ paddedSecs }`;
            }
        }

        return null;
    }

    public static convertFiltersToUrl = (basePath : string, filters : IFilterBar) => {
        let url = basePath;

        url = AppUtils.filterQueryBuildHelper(url, basePath, filters.appliedFilters);
        url = AppUtils.filterQueryBuildHelper(url, basePath, filters.requiredFilters);
        
        // We appended no filters, so we add the question mark 
        // for skip/take and sorts
        if (url === basePath) {
            url += '?';
        }

        // Juuust in case.
        if (filters.paginationInfo.skip < 0) { filters.paginationInfo.skip = 0; }
        if (filters.paginationInfo.take <= 0) { filters.paginationInfo.take = GlobalConstants.DEFAULT_PAGE_SIZE; }
    
        url += `skip=${filters.paginationInfo.skip}&take=${filters.paginationInfo.take}`;

        if (!AppUtils.IsEmpty(filters.selectedSort)) {
            url += `&sortBy=${ filters.selectedSort.sortBy }&sortDir=${ filters.selectedSort.sortDir }`;
        }
    
        return url;

    }

    /**
     * Attempt to find a qsName in the applied filters of the provided
     * instance of the IFilterBar
     * @param filterBar The IFilterBar to search through
     * @param qsName The query string name filter to try and find
     * @returns The string qsValue or undefined if the qsName is not found
     */
    public static getQsValue = (filterBar : IFilterBar, qsName : string) : string|undefined => {
        const creationDateFilter = filterBar.appliedFilters.find((fItm : AppliedFilterBarOpt) => {
            return fItm.qsName === qsName;
        });

        return creationDateFilter?.qsValue;
    }

    /**
     * Converts an enum's corresponding metadata class
     * to an array of FilterBarInputOpt.
     * 
     * @param data The metadata const representing the enum to convert
     */
    public static enumMetadataToFilters(data: any): FilterBarInputOpt[] {

        if (AppUtils.IsEmpty(data)) {
            return [];
        }

        const selects : FilterBarInputOpt[] = [];

        Object.keys(data).forEach((curr : any) => {

            if (typeof curr === "string" && !Number.isNaN(Number(curr))) {
                const enumOpt = data[curr];

                selects.push(new FilterBarInputOpt(
                    enumOpt.display, 
                    false, 
                    enumOpt.value
                ));

            }

        });

        return selects;
    }

    /**
     * Take the supplied filter list and add it to the URL
     * @param url The string URL we're building
     * @param basePath The base URL we're going to hit
     * @param filters The list of filters to add to the URL
     */
    private static filterQueryBuildHelper(url : string, basePath: string, filters : AppliedFilterBarOpt[]) : string {
        if (filters.length > 0) {

            // We appended no optional filters, so we add
            // the question mark before writing our requireds
            if (url === basePath) {
                url += '?';   
            }

            const grouped = AppUtils.groupBy(filters, (f : AppliedFilterBarOpt) => f.qsName);            
            Object.keys(grouped).forEach((qsName: string) => {
                url += `${ qsName }=`;
                grouped[qsName].forEach((itm : AppliedFilterBarOpt) => {
                    url += `${ itm.qsValue },`;
                });

                // Take the trailing comma off
                url = url.substr(0, url.length - 1) + '&';
            });
        }

        return url;
    }

     /**
    * Rollup and return a list of metrics that conform to IPriceSummaryTotal
    * @param orders A list of orders that should be summed
    */
      public static calculatePrices = (orders : IPriceSummaryTotal[]) => {

           const newTotals = new PriceSummaryTotals();
    
           // Add orders on stop
           orders.forEach((itm : IPriceSummaryTotal) => {
               newTotals.subtotal     += itm.subtotal;
               newTotals.tax    += itm.tax;
               newTotals.freight   += itm.freight;
               newTotals.total  += itm.total;
           });
    
           return newTotals;
       }
}
